import { splitMinor } from '@sao/shared/money';
import { cn } from '@/lib/utils';

export interface MoneyProps {
  /** Integer minor units (pesewas or cents). */
  minor: number;
  currency: string;
  /** 'auto' shows - for negatives; 'always' shows + or -; 'none' hides the sign. */
  sign?: 'auto' | 'always' | 'none';
  size?: 'sm' | 'base' | 'lg' | '2xl';
  /** Default true: the minor units in muted ink. */
  dimMinor?: boolean;
  className?: string;
}

const SIZE_CLASSES = {
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg font-semibold',
  '2xl': 'text-2xl',
} as const;

/**
 * Every amount on screen: tabular figures, a muted smaller currency code and
 * dimmed minor units. "GHS 1,234.00". Never wraps, never sums currencies.
 */
export function Money({
  minor,
  currency,
  sign = 'auto',
  size,
  dimMinor = true,
  className,
}: Readonly<MoneyProps>) {
  const { whole, fraction } = splitMinor(Math.abs(minor), currency);
  let mark = '';
  if (sign !== 'none' && minor < 0) mark = '−';
  else if (sign === 'always' && minor > 0) mark = '+';
  return (
    <span
      data-slot="money"
      className={cn(
        'whitespace-nowrap tabular-nums',
        size ? SIZE_CLASSES[size] : null,
        className,
      )}
    >
      {mark}
      <span className="text-[0.85em] font-medium text-muted-foreground">
        {currency}
      </span>{' '}
      <span>{whole}</span>
      <span className={cn(dimMinor && 'text-muted-foreground')}>
        {fraction}
      </span>
    </span>
  );
}
